/**
 * Black Friday → Calendar — /black-friday/calendar/:year
 *
 * Per brand: the stock-order deadline, the window the brand allows for the
 * consumer discount, and the top-up deadline, drawn against our own
 * pre-Black Friday and Black Week. On top: every deadline in the next two
 * weeks, so October's order cut-offs are never missed.
 */
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Card, CardHead } from '../../components/ui/Card';
import { IconButton } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import {
  BrandTerms,
  fmtDay,
  fmtPct,
  num,
  useBFSeason,
  useBFSeasons,
  useBFTerms,
  rememberBFYear,
  rememberedBFYear,
} from '../../lib/api/blackFriday';
import { BrandDot } from './BFRegister';
import { BF_STYLES } from './styles';

const CAL_STYLES = `
.bf .bfcal { position: relative; display: block; }
.bf .cal-row { display: grid; grid-template-columns: 210px 1fr; align-items: center; min-height: 34px; border-bottom: 1px solid var(--border-soft); }
.bf .cal-row:last-child { border-bottom: 0; }
.bf .cal-row.head { min-height: 28px; position: sticky; top: 0; background: var(--bg-elevated); z-index: 3; border-bottom: 1px solid var(--border); }
.bf .cal-name { display: flex; align-items: center; gap: 8px; padding: 4px 12px 4px 16px; font-size: 12.5px; font-weight: 600; cursor: pointer; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.bf .cal-name:hover { text-decoration: underline; }
.bf .cal-name .brand-dot { width: 20px; height: 20px; font-size: 10px; border-radius: 5px; }
.bf .track { position: relative; height: 34px; margin-right: 16px; }
.bf .head .track { height: 28px; }
.bf .tick { position: absolute; top: 0; bottom: 0; border-left: 1px solid var(--border-soft); font-size: 10.5px; color: var(--ink-4); padding: 6px 0 0 4px; white-space: nowrap; }
.bf .tick.month { border-left-color: var(--border); color: var(--ink-3); font-weight: 600; }
.bf .band { position: absolute; top: 0; bottom: 0; pointer-events: none; z-index: 0; }
.bf .band.prebf { background: rgba(255, 189, 9, 0.10); }
.bf .band.week { background: rgba(43, 65, 56, 0.10); }
.bf .today { position: absolute; top: 0; bottom: 0; border-left: 2px solid #D9534F; z-index: 2; pointer-events: none; }
.bf .bar { position: absolute; top: 11px; height: 12px; border-radius: 6px; background: #2E9B57; opacity: 0.85; z-index: 1; min-width: 6px; }
.bf .bar.ours { top: 25px; height: 4px; background: #2B4138; opacity: 0.6; }
.bf .mark { position: absolute; top: 10px; width: 14px; height: 14px; transform: translateX(-7px) rotate(45deg); border-radius: 2px; z-index: 2; }
.bf .mark.order { background: #2F4BCB; }
.bf .mark.topup { background: #E6A800; }
.bf .legend { display: flex; gap: 16px; flex-wrap: wrap; font-size: 12px; color: var(--ink-3); padding: 0 16px 12px; align-items: center; }
.bf .legend i { display: inline-block; width: 12px; height: 12px; vertical-align: -2px; margin-right: 6px; border-radius: 2px; }
.bf .soon { display: grid; gap: 6px; padding: 12px 16px 14px; }
.bf .soon-row { display: grid; grid-template-columns: 90px 18px 1fr; gap: 8px; align-items: center; font-size: 13px; cursor: pointer; }
.bf .soon-row:hover .who { text-decoration: underline; }
.bf .soon-row .when { color: var(--ink-3); font-variant-numeric: tabular-nums; }
.bf .soon-row .who { font-weight: 600; }
`;

const DAY = 86400000;
const toTime = (iso: string) => new Date(`${iso}T00:00:00`).getTime();
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** /black-friday/calendar → the year you were last looking at in the
 *  Black Friday section (register or calendar), else the newest season. */
export const BFCalendarRedirect = () => {
  const seasons = useBFSeasons();
  if (seasons.isLoading) return null;
  const year = rememberedBFYear() ?? seasons.data?.[0]?.year ?? new Date().getFullYear();
  return <Navigate to={`/black-friday/calendar/${year}`} replace />;
};

const datesOf = (t: BrandTerms) =>
  [t.order_deadline, t.promo_start, t.promo_end, t.topup_deadline, t.our_promo_start].filter(Boolean) as string[];

export const BFCalendar = () => {
  const { year: yearParam } = useParams<{ year: string }>();
  const year = parseInt(yearParam || '', 10) || new Date().getFullYear();
  const navigate = useNavigate();
  const season = useBFSeason(year);
  const terms = useBFTerms(year);
  const [onlyDated, setOnlyDated] = useState(true);
  useEffect(() => rememberBFYear(year), [year]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { start, end, rows, undated, soon } = useMemo(() => {
    const list = terms.data ?? [];
    const all = list.flatMap(datesOf).map(toTime);
    const s = season.data;
    [s?.prebf_start, s?.week_end].forEach((d) => d && all.push(toTime(d)));
    const defaultStart = new Date(year, 8, 15).getTime();
    const defaultEnd = new Date(year, 11, 20).getTime();
    const st = Math.min(defaultStart, ...(all.length ? [Math.min(...all) - 3 * DAY] : []));
    const en = Math.max(defaultEnd, ...(all.length ? [Math.max(...all) + 3 * DAY] : []));
    const dated = list.filter((t) => datesOf(t).length > 0);
    const keyDate = (t: BrandTerms) => Math.min(...[t.order_deadline, t.promo_start, t.topup_deadline].filter(Boolean).map((d) => toTime(d as string)), Infinity);
    dated.sort((a, b) => keyDate(a) - keyDate(b) || a.brand_name.localeCompare(b.brand_name));
    const horizon = today.getTime() + 14 * DAY;
    const upcoming: { date: string; kind: 'order' | 'topup' | 'promo'; t: BrandTerms }[] = [];
    list.forEach((t) => {
      if (t.order_deadline) upcoming.push({ date: t.order_deadline, kind: 'order', t });
      if (t.topup_deadline) upcoming.push({ date: t.topup_deadline, kind: 'topup', t });
      if (t.promo_start) upcoming.push({ date: t.promo_start, kind: 'promo', t });
    });
    return {
      start: st,
      end: en,
      rows: dated,
      undated: list.length - dated.length,
      soon: upcoming
        .filter((u) => toTime(u.date) >= today.getTime() && toTime(u.date) <= horizon)
        .sort((a, b) => a.date.localeCompare(b.date)),
    };
  }, [terms.data, season.data, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const span = end - start || 1;
  const pct = (iso: string) => ((toTime(iso) - start) / span) * 100;
  const pctT = (t: number) => ((t - start) / span) * 100;

  const ticks: { left: number; label: string; month: boolean }[] = [];
  const d = new Date(start);
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7)); // next Monday
  for (; d.getTime() <= end; d.setDate(d.getDate() + 7)) {
    ticks.push({ left: pctT(d.getTime()), label: `${d.getDate()}`, month: false });
  }
  const m = new Date(start);
  m.setDate(1);
  m.setMonth(m.getMonth() + 1);
  for (; m.getTime() <= end; m.setMonth(m.getMonth() + 1)) {
    ticks.push({ left: pctT(m.getTime()), label: MONTHS[m.getMonth()], month: true });
  }

  const s = season.data;
  const bands = (
    <>
      {s?.prebf_start && s.prebf_end && <div className="band prebf" style={{ left: `${pct(s.prebf_start)}%`, width: `${pct(s.prebf_end) - pct(s.prebf_start) + 100 / (span / DAY)}%` }} />}
      {s?.week_start && s.week_end && <div className="band week" style={{ left: `${pct(s.week_start)}%`, width: `${pct(s.week_end) - pct(s.week_start) + 100 / (span / DAY)}%` }} />}
      {today.getTime() >= start && today.getTime() <= end && <div className="today" style={{ left: `${pctT(today.getTime())}%` }} />}
    </>
  );
  const shown = onlyDated ? rows : [...rows, ...(terms.data ?? []).filter((t) => datesOf(t).length === 0)];
  const KIND = { order: 'Stock-order deadline', topup: 'Top-up deadline', promo: 'Brand allows the discount from' };

  return (
    <div className="bf">
      <style>{BF_STYLES + CAL_STYLES}</style>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Black Friday</div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconButton aria-label="Previous season" onClick={() => navigate(`/black-friday/calendar/${year - 1}`)}><Icon name="chevronLeft" size={14} /></IconButton>
            Calendar {year}
            <IconButton aria-label="Next season" onClick={() => navigate(`/black-friday/calendar/${year + 1}`)}><Icon name="chevronRight" size={14} /></IconButton>
          </h1>
        </div>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <CardHead title="Next two weeks" sub="Order and top-up deadlines, and brands whose discount window opens." />
        <div className="soon">
          {soon.length === 0 ? (
            <div className="hint">Nothing in the next 14 days{(terms.data ?? []).length ? '' : ' (no brands on this season yet)'}.</div>
          ) : soon.map((u, i) => (
            <div key={i} className="soon-row" onClick={() => navigate(`/black-friday/${year}/${u.t.brand}`)}>
              <span className="when">{fmtDay(u.date)}</span>
              <span className={`mark ${u.kind}`} style={{ position: 'static', transform: 'rotate(45deg)', width: 10, height: 10, background: u.kind === 'promo' ? '#2E9B57' : undefined }} />
              <span><span className="who">{u.t.brand_name}</span> <span className="muted">· {KIND[u.kind]}</span> <span className="muted">→</span></span>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="legend" style={{ paddingTop: 12 }}>
          <span><i style={{ background: '#2F4BCB', transform: 'rotate(45deg)' }} />Stock-order deadline</span>
          <span><i style={{ background: '#2E9B57' }} />Window the brand allows</span>
          <span><i style={{ background: '#2B4138', height: 4, opacity: 0.6 }} />When we went live</span>
          <span><i style={{ background: '#E6A800', transform: 'rotate(45deg)' }} />Top-up deadline</span>
          <span><i style={{ background: 'rgba(255,189,9,0.25)' }} />Our pre-Black Friday</span>
          <span><i style={{ background: 'rgba(43,65,56,0.2)' }} />Our Black Week</span>
          <span><i style={{ background: '#D9534F', width: 2 }} />Today</span>
          <span style={{ marginLeft: 'auto' }}>
            <button type="button" className={`filter-pill ${onlyDated ? 'active' : ''}`} onClick={() => setOnlyDated((v) => !v)}>
              Only brands with dates{undated ? ` (${undated} without)` : ''}
            </button>
          </span>
        </div>
        {terms.isLoading || season.isLoading ? (
          <div className="empty">Loading…</div>
        ) : !s ? (
          <div className="empty">There is no Black Friday {year} season yet.</div>
        ) : shown.length === 0 ? (
          <div className="empty">No dates entered for {year} yet. Fill in order deadlines and promo windows on the brand pages.</div>
        ) : (
          <div className="bfcal">
            <div className="cal-row head">
              <div className="cal-name" style={{ cursor: 'default' }}>{shown.length} brands</div>
              <div className="track">
                {ticks.map((t, i) => <div key={i} className={`tick ${t.month ? 'month' : ''}`} style={{ left: `${t.left}%` }}>{t.label}</div>)}
                {bands}
              </div>
            </div>
            {shown.map((t) => {
              const tip = [
                t.order_deadline && `Order by ${fmtDay(t.order_deadline)}`,
                (t.promo_start || t.promo_end) && `Allowed ${fmtDay(t.promo_start) || '?'} – ${fmtDay(t.promo_end) || '?'}${num(t.discount_max_pct) !== null ? ` (max ${fmtPct(t.discount_max_pct)})` : ''}`,
                t.topup_deadline && `Top-up by ${fmtDay(t.topup_deadline)}`,
                t.our_promo_start && `We went live ${fmtDay(t.our_promo_start)}`,
              ].filter(Boolean).join('\n');
              return (
                <div key={t.id} className="cal-row" title={tip}>
                  <div className="cal-name" onClick={() => navigate(`/black-friday/${year}/${t.brand}`)}>
                    <BrandDot name={t.brand_name} logo={t.brand_logo} color={t.brand_color} />
                    {t.brand_name}
                  </div>
                  <div className="track">
                    {bands}
                    {(t.promo_start || t.promo_end) && (
                      <div className="bar" style={{
                        left: `${pct((t.promo_start || t.promo_end)!)}%`,
                        width: `${Math.max(0, pct((t.promo_end || t.promo_start)!) - pct((t.promo_start || t.promo_end)!)) + 100 / (span / DAY)}%`,
                      }} />
                    )}
                    {t.our_promo_start && <div className="bar ours" style={{ left: `${pct(t.our_promo_start)}%`, width: `${Math.max(1, (t.promo_end ? pct(t.promo_end) : pct(t.our_promo_start) + 2) - pct(t.our_promo_start))}%` }} />}
                    {t.order_deadline && <div className="mark order" style={{ left: `${pct(t.order_deadline)}%` }} />}
                    {t.topup_deadline && <div className="mark topup" style={{ left: `${pct(t.topup_deadline)}%` }} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default BFCalendar;
